import { z } from 'zod';

import { PageQuerySchema, SearchQuerySchema } from '../../shared/pagination';
import { AgentVersionSchema } from '../agents';
import { MessageSchema } from './messages';

// A session is a trace/test conversation pinned to one immutable AgentVersion; its
// transcript is the append-only log of Messages (see ./messages).

// Run-state of a session, not an archive lifecycle. Mirrors the console's
// Idle / Active states plus a terminal failure. `interrupted` is a resumable pause, not a
// failure: the process running the loop caught SIGTERM and wrote down which sessions it was
// holding, so the transcript stops at a known point instead of a `running` row nobody owns.
export const SESSION_STATUSES = ['idle', 'running', 'failed', 'interrupted'] as const;
export const SessionStatusSchema = z.enum(SESSION_STATUSES);
export type SessionStatus = z.infer<typeof SessionStatusSchema>;

// How a session was created relative to another: agent-as-tool nesting (`spawn`)
// or a handoff successor (`handoff`). Mirrors agent_sessions.origin_kind.
export const ORIGIN_KINDS = ['spawn', 'handoff'] as const;
export const OriginKindSchema = z.enum(ORIGIN_KINDS);
export type OriginKind = z.infer<typeof OriginKindSchema>;

export const SessionSchema = z.object({
  id: z.uuid(),
  agentId: z.uuid(),
  agentVersionId: z.uuid(), // pinned immutable snapshot the loop runs against
  name: z.string().max(120).nullable(),
  status: SessionStatusSchema,
  // the admin who created this session, resolved to their email. Nullable: gateway-
  // originated sessions (future) have no admin creator. Server-derived, never in the
  // write contract.
  createdByEmail: z.email().nullable(),
  // self-referential origin edge (see agent_sessions): how this session was created.
  // null for a root (user/gateway) session; set for a spawned sub-run or handoff successor.
  originSessionId: z.uuid().nullable(),
  originKind: OriginKindSchema.nullable(),
  // running sums over response messages' usage — denormalized for the list column.
  tokensIn: z.number().int().nonnegative(),
  tokensOut: z.number().int().nonnegative(),
  // the cached slice of tokensIn, not an addend: Gemini's promptTokenCount already
  // includes it, so uncached input is tokensIn - cacheReadTokens.
  cacheReadTokens: z.number().int().nonnegative(),
  error: z.string().nullable(), // last run error when status === 'failed'
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Session = z.infer<typeof SessionSchema>;

// --- Read models for the admin UI ---

// list row: session + the pinned agent resolved (name, slug, version) + a count.
export const SessionSummarySchema = SessionSchema.extend({
  agentName: z.string(),
  agentSlug: z.string(),
  agentVersionNumber: z.number().int().positive(),
  messageCount: z.number().int().nonnegative(),
});
export type SessionSummary = z.infer<typeof SessionSummarySchema>;

// detail page: session + the full pinned version + the ordered transcript + lineage.
export const SessionDetailSchema = SessionSchema.extend({
  agentVersion: AgentVersionSchema,
  messages: z.array(MessageSchema), // ordered by seq asc
  // lineage: the parent/predecessor this session derived from (null for a root), and
  // its direct spawned sub-runs / handoff successor. Populated from the origin edge.
  origin: z.object({ id: z.uuid(), name: z.string().max(120).nullable() }).nullable(),
  children: z.array(
    z.object({
      id: z.uuid(),
      name: z.string().max(120).nullable(),
      originKind: OriginKindSchema,
      status: SessionStatusSchema,
    }),
  ),
});
export type SessionDetail = z.infer<typeof SessionDetailSchema>;

// --- Write contracts ---

// create a session: pick an agent; version defaults to that agent's active version.
export const CreateSessionSchema = z.object({
  agentId: z.uuid(),
  name: z.string().max(120).optional(),
  versionId: z.uuid().optional(), // pin a specific version; omitted => active version
});
export type CreateSession = z.infer<typeof CreateSessionSchema>;

// append ONE user message (does not run the loop). Repeat to queue several.
export const PostMessageSchema = z.object({
  content: z.string().min(1),
});
export type PostMessage = z.infer<typeof PostMessageSchema>;

// run the loop over the current history — no body. (409 if already running.)

// list = paging + search + optional filters (mirrors the console filter bar).
export const ListSessionsQuerySchema = PageQuerySchema.merge(SearchQuerySchema).extend({
  agentId: z.uuid().optional(),
  status: SessionStatusSchema.optional(),
});
export type ListSessionsQuery = z.infer<typeof ListSessionsQuerySchema>;

export const SessionPageSchema = z.object({
  items: z.array(SessionSummarySchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
});
export type SessionPage = z.infer<typeof SessionPageSchema>;
